import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { localizeName } from "../lib/catI18n";
import { PageTitle, btn, input, selectCls, label } from "../lib/ui";
import { Camera, Upload, FileText, Image as ImageIcon, Smartphone, Home, Briefcase, CheckCircle2, X, Eye, Trash2, Pencil } from "lucide-react";

const CAP = {
  es: { title: "Subir ticket o factura", sub: "Haz una foto o adjunta un archivo y conviértelo en un gasto.", photo: "Hacer foto", photoHint: "Usa la cámara del móvil", file: "Subir archivo", fileHint: "Imagen o PDF (máx. 5 MB)", uploading: "Guardando…", recent: "Subidas recientes", empty: "Aún no has subido nada.", install: "Instalar app", tooBig: "La foto es demasiado grande incluso comprimida. Hazla con menos resolución.", tooBigPdf: "El PDF supera los 10 MB. Súbelo desde el ordenador o redúcelo.", readOnly: "El contable tiene acceso de solo lectura.",
    scopeQ: "¿Personal o profesional?", personal: "Personal", professional: "Profesional", pickScope: "Elige primero si es personal o profesional.",
    amount: "Importe", date: "Fecha", merchant: "Comercio o descripción", merchantPh: "Ej.: Supermercado, gasolina…", account: "Cuenta", none: "—", category: "Categoría (opcional)", currency: "Moneda",
    saveMov: "Guardar como gasto", saveDoc: "Guardar solo como documento", cancel: "Cancelar", selected: "Archivo",
    okMov: "Gasto creado y ticket adjuntado.", okDoc: "Guardado en tu cuenta.", another: "Subir otro ticket", viewMov: "Ver en movimientos", ticket: "Ticket", open: "Ver / Abrir", assign: "Asignar cuenta", close: "Cerrar",
    del: "Eliminar", delQ: "¿Eliminar este documento?", delLinked: "Este documento está ligado a un gasto. ¿Qué quieres eliminar?", delDocOnly: "Eliminar solo el documento", delDocTx: "Eliminar documento y gasto", delOk: "Documento eliminado.", delOkTx: "Documento y gasto eliminados.",
    nameLbl: "Nombre (opcional)", namePh: "Ej.: Factura luz enero", rename: "Renombrar", renameTitle: "Nombre del documento", save: "Guardar", renamed: "Nombre actualizado." },
  en: { title: "Upload receipt or invoice", sub: "Take a photo or attach a file and turn it into an expense.", photo: "Take a photo", photoHint: "Use your phone camera", file: "Upload file", fileHint: "Image or PDF (max 5 MB)", uploading: "Saving…", recent: "Recent uploads", empty: "Nothing uploaded yet.", install: "Install app", tooBig: "The photo is too large even after compression. Take it at a lower resolution.", tooBigPdf: "The PDF exceeds 10 MB. Upload it from a computer or reduce its size.", readOnly: "The accountant has read-only access.",
    scopeQ: "Personal or business?", personal: "Personal", professional: "Business", pickScope: "First choose personal or business.",
    amount: "Amount", date: "Date", merchant: "Merchant or description", merchantPh: "E.g. Groceries, fuel…", account: "Account", none: "—", category: "Category (optional)", currency: "Currency",
    saveMov: "Save as expense", saveDoc: "Save as document only", cancel: "Cancel", selected: "File",
    okMov: "Expense created and receipt attached.", okDoc: "Saved to your account.", another: "Upload another receipt", viewMov: "See in movements", ticket: "Receipt", open: "View / Open", assign: "Assign account", close: "Close",
    del: "Delete", delQ: "Delete this document?", delLinked: "This document is linked to an expense. What do you want to delete?", delDocOnly: "Delete document only", delDocTx: "Delete document and expense", delOk: "Document deleted.", delOkTx: "Document and expense deleted.",
    nameLbl: "Name (optional)", namePh: "E.g. Electricity invoice Jan", rename: "Rename", renameTitle: "Document name", save: "Save", renamed: "Name updated." },
  fr: { title: "Ajouter un reçu ou une facture", sub: "Prenez une photo ou joignez un fichier et transformez-le en dépense.", photo: "Prendre une photo", photoHint: "Utilisez l'appareil photo", file: "Téléverser un fichier", fileHint: "Image ou PDF (max 5 Mo)", uploading: "Enregistrement…", recent: "Ajouts récents", empty: "Rien d'ajouté pour l'instant.", install: "Installer l'app", tooBig: "La photo est trop lourde même compressée. Prenez-la en plus basse résolution.", tooBigPdf: "Le PDF dépasse 10 Mo. Téléversez-le depuis un ordinateur ou réduisez-le.", readOnly: "Le comptable a un accès en lecture seule.",
    scopeQ: "Personnel ou professionnel ?", personal: "Personnel", professional: "Professionnel", pickScope: "Choisissez d'abord personnel ou professionnel.",
    amount: "Montant", date: "Date", merchant: "Commerçant ou description", merchantPh: "Ex. : Supermarché, carburant…", account: "Compte", none: "—", category: "Catégorie (optionnel)", currency: "Monnaie",
    saveMov: "Enregistrer comme dépense", saveDoc: "Enregistrer comme document seulement", cancel: "Annuler", selected: "Fichier",
    okMov: "Dépense créée et reçu joint.", okDoc: "Enregistré dans votre compte.", another: "Ajouter un autre reçu", viewMov: "Voir dans les mouvements", ticket: "Reçu", open: "Voir / Ouvrir", assign: "Attribuer un compte", close: "Fermer",
    del: "Supprimer", delQ: "Supprimer ce document ?", delLinked: "Ce document est lié à une dépense. Que voulez-vous supprimer ?", delDocOnly: "Supprimer le document seulement", delDocTx: "Supprimer le document et la dépense", delOk: "Document supprimé.", delOkTx: "Document et dépense supprimés.",
    nameLbl: "Nom (optionnel)", namePh: "Ex. : Facture électricité janv.", rename: "Renommer", renameTitle: "Nom du document", save: "Enregistrer", renamed: "Nom mis à jour." },
  pt: { title: "Enviar recibo ou fatura", sub: "Tire uma foto ou anexe um ficheiro e transforme-o num gasto.", photo: "Tirar foto", photoHint: "Use a câmara do telemóvel", file: "Enviar ficheiro", fileHint: "Imagem ou PDF (máx. 5 MB)", uploading: "A guardar…", recent: "Envios recentes", empty: "Ainda não enviou nada.", install: "Instalar app", tooBig: "A foto é demasiado grande mesmo comprimida. Tire-a com menos resolução.", tooBigPdf: "O PDF excede 10 MB. Envie-o a partir do computador ou reduza-o.", readOnly: "O contabilista tem acesso só de leitura.",
    scopeQ: "Pessoal ou profissional?", personal: "Pessoal", professional: "Profissional", pickScope: "Escolha primeiro se é pessoal ou profissional.",
    amount: "Valor", date: "Data", merchant: "Comércio ou descrição", merchantPh: "Ex.: Supermercado, combustível…", account: "Conta", none: "—", category: "Categoria (opcional)", currency: "Moeda",
    saveMov: "Guardar como gasto", saveDoc: "Guardar só como documento", cancel: "Cancelar", selected: "Ficheiro",
    okMov: "Gasto criado e recibo anexado.", okDoc: "Guardado na sua conta.", another: "Enviar outro recibo", viewMov: "Ver nos movimentos", ticket: "Recibo", open: "Ver / Abrir", assign: "Atribuir conta", close: "Fechar",
    del: "Eliminar", delQ: "Eliminar este documento?", delLinked: "Este documento está ligado a um gasto. O que quer eliminar?", delDocOnly: "Eliminar só o documento", delDocTx: "Eliminar documento e gasto", delOk: "Documento eliminado.", delOkTx: "Documento e gasto eliminados.",
    nameLbl: "Nome (opcional)", namePh: "Ex.: Fatura luz janeiro", rename: "Renomear", renameTitle: "Nome do documento", save: "Guardar", renamed: "Nome atualizado." },
  it: { title: "Carica scontrino o fattura", sub: "Scatta una foto o allega un file e trasformalo in una spesa.", photo: "Scatta una foto", photoHint: "Usa la fotocamera del telefono", file: "Carica file", fileHint: "Immagine o PDF (max 5 MB)", uploading: "Salvataggio…", recent: "Caricamenti recenti", empty: "Non hai ancora caricato nulla.", install: "Installa l'app", tooBig: "La foto è troppo grande anche compressa. Scattala a risoluzione più bassa.", tooBigPdf: "Il PDF supera i 10 MB. Caricalo dal computer o riducilo.", readOnly: "Il commercialista ha accesso in sola lettura.",
    scopeQ: "Personale o professionale?", personal: "Personale", professional: "Professionale", pickScope: "Scegli prima personale o professionale.",
    amount: "Importo", date: "Data", merchant: "Esercente o descrizione", merchantPh: "Es.: Supermercato, carburante…", account: "Conto", none: "—", category: "Categoria (opzionale)", currency: "Valuta",
    saveMov: "Salva come spesa", saveDoc: "Salva solo come documento", cancel: "Annulla", selected: "File",
    okMov: "Spesa creata e scontrino allegato.", okDoc: "Salvato nel tuo account.", another: "Carica un altro scontrino", viewMov: "Vedi nei movimenti", ticket: "Scontrino", open: "Vedi / Apri", assign: "Assegna conto", close: "Chiudi",
    del: "Elimina", delQ: "Eliminare questo documento?", delLinked: "Questo documento è collegato a una spesa. Cosa vuoi eliminare?", delDocOnly: "Elimina solo il documento", delDocTx: "Elimina documento e spesa", delOk: "Documento eliminato.", delOkTx: "Documento e spesa eliminati.",
    nameLbl: "Nome (opzionale)", namePh: "Es.: Fattura luce gennaio", rename: "Rinomina", renameTitle: "Nome del documento", save: "Salva", renamed: "Nome aggiornato." },
};

const today = () => new Date().toISOString().slice(0, 10);

const OXM = {
  es: { scan: "Leyendo ticket con IA…", dup: "Ya existe un gasto con esta fecha, importe y comercio. Revísalo antes de guardar." },
  en: { scan: "Reading receipt with AI…", dup: "An expense with the same date, amount and merchant already exists. Review before saving." },
  fr: { scan: "Lecture du reçu avec IA…", dup: "Une dépense avec la même date, le même montant et le même commerçant existe déjà. Vérifiez avant d'enregistrer." },
  pt: { scan: "A ler o recibo com IA…", dup: "Já existe um gasto com a mesma data, valor e comércio. Reveja antes de guardar." },
  it: { scan: "Lettura scontrino con IA…", dup: "Esiste già una spesa con stessa data, importo ed esercente. Controlla prima di salvare." },
};

// Nombre legible: si es numérico/largo, muestra "Ticket DD/MM/AAAA".
function friendlyName(d, C) {
  const fn = d.filename || "";
  const base = fn.replace(/\.[^.]+$/, "");
  const numericLong = /^[0-9_\-\s]+$/.test(base) || base.length > 40;
  if (numericLong) {
    const dt = d.created_at ? new Date(d.created_at) : new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${C.ticket} ${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
  }
  return fn;
}

// Comprime fotos no cliente: lado maior máx. 1600px, JPEG 0,8. PDFs e no-imagens passam tal como estão.
function compressImage(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        let w = img.width, h = img.height;
        if (w > max || h > max) {
          if (w >= h) { h = Math.round((h * max) / w); w = max; }
          else { w = Math.round((w * max) / h); h = max; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) { resolve(file); return; }
          const name = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
          resolve(new File([blob], name, { type: "image/jpeg" }));
        }, "image/jpeg", 0.8);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    } catch (e) { resolve(file); }
  });
}

export default function Capture() {
  const { user, workspace } = useAuth();
  const { locale } = useI18n();
  const C = CAP[locale] || CAP.es;
  const OX = OXM[locale] || OXM.es;
  const readOnly = user?.role === "accountant";
  const baseCur = workspace?.base_currency || "EUR";

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [installEvt, setInstallEvt] = useState(null);
  const [done, setDone] = useState(false);
  const [cats, setCats] = useState([]);
  const [accts, setAccts] = useState([]);
  const [form, setForm] = useState({ scope: "", amount: "", date: today(), description: "", account_id: "", category_id: "", currency_code: baseCur, name: "", activity_id: "" });
  const [viewer, setViewer] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [delDoc, setDelDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [acts, setActs] = useState([]);
  const [ocrOn, setOcrOn] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [dupWarn, setDupWarn] = useState(false);
  const actsActive = acts.filter((a) => !a.archived);
  const CAPACT = ({ es: "Actividad (opcional)", en: "Activity (optional)", fr: "Activité (optionnel)", pt: "Atividade (opcional)", it: "Attività (opzionale)" })[locale] || "Actividad (opcional)";
  const CAPGEN = ({ es: "General / compartida", en: "General / shared", fr: "Général / partagé", pt: "Geral / partilhada", it: "Generale / condivisa" })[locale] || "General / compartida";
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/documents"); setRecent(data.documents || []); } catch (e) { console.warn("No se pudieron cargar los documentos:", e); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/accounts"), api.get("/activities"), api.get("/import/ocr-setting")])
      .then(([c, a, ac, o]) => { setCats(c.data || []); setAccts(a.data || []); setActs(ac.data || []); setOcrOn(!!o.data?.enabled); })
      .catch((e) => console.warn("No se pudieron cargar categorías/cuentas:", e));
  }, []);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const resetForm = () => setForm({ scope: "", amount: "", date: today(), description: "", account_id: "", category_id: "", currency_code: baseCur, name: "", activity_id: "" });
  const pickFile = async (f) => {
    if (!f) return;
    const isPdf = String(f.type).includes("pdf") || /\.pdf$/i.test(f.name || "");
    let finalFile = f;
    if (!isPdf && String(f.type).startsWith("image/")) finalFile = await compressImage(f);
    const maxBytes = isPdf ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (finalFile.size > maxBytes) {
      toast.error(isPdf ? C.tooBigPdf : C.tooBig);
      if (camRef.current) camRef.current.value = ""; if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setDone(false); setFile(finalFile); setPreview(isPdf ? null : URL.createObjectURL(finalFile)); resetForm(); setDupWarn(false);
    if (!isPdf && String(finalFile.type).startsWith("image/") && ocrOn) runReceiptOcr(finalFile);
  };
  const clearPick = () => { if (preview) URL.revokeObjectURL(preview); setFile(null); setPreview(null); resetForm(); setDupWarn(false); if (camRef.current) camRef.current.value = ""; if (fileRef.current) fileRef.current.value = ""; };

  const runReceiptOcr = async (f) => {
    setOcrBusy(true); setDupWarn(false);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/documents/ocr-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({
        ...prev,
        description: data.merchant || prev.description,
        date: data.date || prev.date,
        amount: data.amount || prev.amount,
        currency_code: data.currency || prev.currency_code,
        category_id: data.category_id || prev.category_id,
      }));
      if (data.duplicate) setDupWarn(true);
    } catch (e) { /* OCR opcional: el formulario manual sigue igual */ } finally { setOcrBusy(false); }
  };
  const setScope = (s) => setForm((f) => ({ ...f, scope: s, account_id: "", category_id: "", activity_id: "" }));

  const openDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (String(d.mime || "").includes("pdf")) { window.open(url, "_blank", "noopener"); }
      else { setZoom(false); setViewer({ ...d, url }); }
    } catch (e) { toast.error(apiError(e)); }
  };
  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };
  const assignScope = async (d, scope) => {
    setAssigning(d.id);
    try {
      await api.patch(`/documents/${d.id}`, { scope });
      setRecent((r) => r.map((x) => (x.id === d.id ? { ...x, scope } : x)));
    } catch (e) { toast.error(apiError(e)); } finally { setAssigning(null); }
  };

  const removeDoc = async (withTx) => {
    const d = delDoc;
    if (!d) return;
    setDeleting(true);
    try {
      await api.delete(`/documents/${d.id}`, { params: { delete_tx: withTx } });
      setRecent((r) => r.filter((x) => x.id !== d.id));
      setDelDoc(null);
      toast.success(withTx ? C.delOkTx : C.delOk);
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const scopeAccts = accts.filter((a) => a.scope === form.scope);
  const scopeCats = cats.filter((c) => c.kind === "expense" && !c.archived && (c.scope === form.scope || c.scope === "shared"));

  const uploadDoc = async () => {
    const fd = new FormData();
    fd.append("file", file);
    if (form.name && form.name.trim()) fd.append("name", form.name.trim());
    const { data } = await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data; // {id, filename, name, size, mime}
  };

  const doRename = async () => {
    const d = renameDoc;
    if (!d) return;
    setRenaming(true);
    try {
      const { data } = await api.patch(`/documents/${d.id}`, { name: renameVal.trim() });
      setRecent((r) => r.map((x) => (x.id === d.id ? { ...x, name: data.name } : x)));
      setRenameDoc(null);
      toast.success(C.renamed);
    } catch (e) { toast.error(apiError(e)); } finally { setRenaming(false); }
  };

  const saveMovement = async () => {
    if (!form.scope) { toast.error(C.pickScope); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error(C.amount); return; }
    setBusy(true);
    try {
      const doc = await uploadDoc();
      const payload = {
        date: form.date, type: "expense", amount: String(form.amount), currency_code: form.currency_code,
        scope: form.scope, description: form.description || file.name, payment_status: "paid",
        account_id: form.account_id || null, category_id: form.category_id || null, document_id: doc.id,
        activity_id: (form.scope === "personal" || form.scope === "professional") ? (form.activity_id || null) : null,
      };
      await api.post("/transactions", payload);
      await load();
      setFile(null); setPreview(null); resetForm(); setDone("movement");
      if (camRef.current) camRef.current.value = ""; if (fileRef.current) fileRef.current.value = "";
      toast.success(C.okMov);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveDocOnly = async () => {
    setBusy(true);
    try {
      await uploadDoc();
      await load();
      setFile(null); setPreview(null); resetForm(); setDone("doc");
      if (camRef.current) camRef.current.value = ""; if (fileRef.current) fileRef.current.value = "";
      toast.success(C.okDoc);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const doInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try { await installEvt.userChoice; } catch (e) { console.warn("Instalacion PWA cancelada o fallida:", e); }
    setInstallEvt(null);
  };

  const scopeBtn = (v, Icon, lbl) => {
    const active = form.scope === v;
    return (
      <button type="button" data-testid={`capture-scope-${v}`} onClick={() => setScope(v)}
        className={`flex-1 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 p-5 transition-colors ${active
          ? "border-[#0299A0] bg-[#0299A0]/10 text-[#075E62]"
          : "border-[#D5E7E5] bg-white text-[#5F8588] hover:border-[#0299A0]/50"}`}>
        <Icon size={26} className={active ? "text-[#0299A0]" : "text-[#5F8588]"} />
        <span className="font-bold text-sm">{lbl}</span>
      </button>
    );
  };

  return (
    <div data-testid="capture-page" className="max-w-xl mx-auto">
      <PageTitle title={C.title} subtitle={C.sub} />

      {readOnly ? (
        <div className="rounded-xl border border-[#D5E7E5] bg-white p-6 text-sm text-[#5F8588]" data-testid="capture-readonly">{C.readOnly}</div>
      ) : done ? (
        <div className="rounded-2xl border border-[#0299A0]/40 bg-[#0299A0]/8 p-6 text-center" data-testid="capture-success">
          <CheckCircle2 size={40} className="text-[#0299A0] mx-auto mb-3" />
          <p className="font-bold text-[#075E62]">{done === "movement" ? C.okMov : C.okDoc}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-5">
            <button onClick={() => setDone(false)} className={btn.accent} data-testid="capture-another"><Camera size={16} /> {C.another}</button>
            <a href="/movements" className={btn.outline} data-testid="capture-go-movements">{C.viewMov}</a>
          </div>
        </div>
      ) : !file ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label data-testid="capture-camera" className="cursor-pointer rounded-2xl border-2 border-dashed border-[#0299A0]/50 bg-[#0299A0]/5 hover:bg-[#0299A0]/10 transition-colors p-8 flex flex-col items-center justify-center gap-3 text-center">
            <Camera size={40} className="text-[#0299A0]" />
            <span className="font-bold text-[#075E62]">{C.photo}</span>
            <span className="text-xs text-[#5F8588]">{C.photoHint}</span>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => pickFile(e.target.files?.[0])} />
          </label>
          <label data-testid="capture-file" className="cursor-pointer rounded-2xl border-2 border-dashed border-[#D5E7E5] bg-white hover:bg-[#F7FBFA] transition-colors p-8 flex flex-col items-center justify-center gap-3 text-center">
            <Upload size={40} className="text-[#075E62]" />
            <span className="font-bold text-[#075E62]">{C.file}</span>
            <span className="text-xs text-[#5F8588]">{C.fileHint}</span>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" disabled={busy} onChange={(e) => pickFile(e.target.files?.[0])} />
          </label>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#D5E7E5] bg-white p-5" data-testid="capture-form">
          <div className="flex items-center gap-2 text-sm text-[#5F8588] mb-5 min-w-0">
            {String(file.type).includes("pdf") ? <FileText size={18} className="text-[#A85751] shrink-0" /> : <ImageIcon size={18} className="text-[#0299A0] shrink-0" />}
            <span className="truncate text-[#075E62] flex-1">{C.selected}: {file.name}</span>
            <button type="button" onClick={clearPick} data-testid="capture-cancel" className="p-1 text-[#5F8588] hover:text-[#A85751] shrink-0"><X size={18} /></button>
          </div>
          {preview && <img src={preview} alt="" data-testid="capture-preview" className="w-full max-h-52 object-contain rounded-xl border border-[#D5E7E5] bg-[#F7FBFA] mb-5" />}
          {ocrBusy && <p className="text-sm text-[#0299A0] mb-3 font-medium" data-testid="capture-ocr-busy">{OX.scan}</p>}
          {dupWarn && <p className="text-sm text-[#B4533F] bg-[#B4533F]/10 rounded-md px-3 py-2 mb-4" data-testid="capture-dup-warn">{OX.dup}</p>}

          <p className="text-base font-bold text-[#075E62] mb-3">{C.scopeQ}</p>
          <div className="flex gap-3 mb-5">
            {scopeBtn("personal", Home, C.personal)}
            {scopeBtn("professional", Briefcase, C.professional)}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className={label}>{C.amount}</label>
              <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={input + " tnum"} placeholder="0.00" data-testid="capture-amount" /></div>
            <div><label className={label}>{C.currency}</label>
              <select value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value })} className={selectCls} data-testid="capture-currency">{["EUR", "CHF", "USD", "GBP", "BRL"].map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className={label}>{C.date}</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={input} data-testid="capture-date" /></div>
            {form.scope && scopeAccts.length > 0 && (
              <div><label className={label}>{C.account}</label>
                <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} className={selectCls} data-testid="capture-account"><option value="">{C.none}</option>{scopeAccts.map((a) => <option key={a.id} value={a.id}>{localizeName(a.name, locale)}</option>)}</select></div>
            )}
            <div className="col-span-2"><label className={label}>{C.merchant}</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} placeholder={C.merchantPh} data-testid="capture-merchant" /></div>
            <div className="col-span-2"><label className={label}>{C.nameLbl}</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} placeholder={C.namePh} data-testid="capture-name" /></div>
            {form.scope && (
              <div className="col-span-2"><label className={label}>{C.category}</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className={selectCls} data-testid="capture-category"><option value="">{C.none}</option>{scopeCats.map((c) => <option key={c.id} value={c.id}>{localizeName(c.name, locale)}</option>)}</select></div>
            )}
            {(form.scope === "personal" || form.scope === "professional") && (
              <div className="col-span-2"><label className={label}>{CAPACT}</label>
                <select value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} className={selectCls} data-testid="capture-activity"><option value="">{CAPGEN}</option>{actsActive.filter((a) => (a.scope || "professional") === form.scope).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-6">
            <button type="button" onClick={saveMovement} disabled={busy || !form.scope || !form.amount} className={btn.accent + " w-full"} data-testid="capture-save-movement">
              <CheckCircle2 size={16} /> {busy ? C.uploading : C.saveMov}
            </button>
            <button type="button" onClick={saveDocOnly} disabled={busy} className={btn.outline + " w-full"} data-testid="capture-save-doc-only">{C.saveDoc}</button>
          </div>
        </div>
      )}

      {busy && <p className="text-sm text-[#0299A0] mt-4 font-medium" data-testid="capture-uploading">{C.uploading}</p>}

      {installEvt && (
        <button onClick={doInstall} data-testid="capture-install" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#075E62] text-white px-4 py-2 text-sm font-semibold hover:bg-[#05787E] transition-colors">
          <Smartphone size={16} /> {C.install}
        </button>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-bold text-[#075E62] mb-3">{C.recent}</h3>
        {recent.length === 0 ? (
          <p className="text-sm text-[#5F8588]">{C.empty}</p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="capture-recent">
            {recent.slice(0, 20).map((d) => {
              const isPdf = String(d.mime || "").includes("pdf");
              const name = d.name || friendlyName(d, C);
              return (
                <li key={d.id} data-testid={`recent-${d.id}`} className="flex items-center gap-3 rounded-xl border border-[#D5E7E5] bg-white p-2.5">
                  <button type="button" onClick={() => openDoc(d)} data-testid={`recent-open-${d.id}`} aria-label={C.open}
                    className="shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-[#D5E7E5] bg-[#F7FBFA] flex items-center justify-center">
                    {isPdf ? <FileText size={22} className="text-[#A85751]" /> : <img src={`${API}/documents/${d.id}/file`} alt="" loading="lazy" className="w-full h-full object-cover" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate font-semibold text-[#075E62] text-sm">{name}</span>
                      {d.scope && <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${d.scope === "professional" ? "bg-[#075E62]/10 text-[#075E62]" : "bg-[#0299A0]/10 text-[#0299A0]"}`}>{d.scope === "professional" ? C.professional : C.personal}</span>}
                    </div>
                    {d.amount || d.merchant ? (
                      <div className="text-xs text-[#5F8588] truncate">{d.merchant}{d.amount ? `${d.merchant ? " · " : ""}${Number(d.amount).toFixed(2)} ${d.currency_code || ""}` : ""}</div>
                    ) : (
                      <div className="text-xs text-[#5F8588] tnum">{Math.round((d.size || 0) / 1024)} KB</div>
                    )}
                    {!d.scope && (
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[11px] text-[#5F8588]">{C.assign}:</span>
                        <button type="button" disabled={assigning === d.id} onClick={() => assignScope(d, "personal")} data-testid={`assign-personal-${d.id}`} className="rounded-full border border-[#0299A0]/40 text-[#0299A0] px-2 py-0.5 text-[11px] font-semibold hover:bg-[#0299A0]/10">{C.personal}</button>
                        <button type="button" disabled={assigning === d.id} onClick={() => assignScope(d, "professional")} data-testid={`assign-professional-${d.id}`} className="rounded-full border border-[#075E62]/40 text-[#075E62] px-2 py-0.5 text-[11px] font-semibold hover:bg-[#075E62]/10">{C.professional}</button>
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => openDoc(d)} data-testid={`recent-view-${d.id}`} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#075E62] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#05787E] transition-colors"><Eye size={14} /> {C.open}</button>
                  <button type="button" onClick={() => { setRenameVal(name); setRenameDoc(d); }} data-testid={`recent-rename-${d.id}`} aria-label={C.rename} title={C.rename} className="shrink-0 inline-flex items-center justify-center rounded-full border border-[#D5E7E5] text-[#5F8588] w-8 h-8 hover:text-[#075E62] hover:border-[#075E62] transition-colors"><Pencil size={15} /></button>
                  <button type="button" onClick={() => setDelDoc(d)} data-testid={`recent-delete-${d.id}`} aria-label={C.del} className="shrink-0 inline-flex items-center justify-center rounded-full border border-[#A85751]/40 text-[#A85751] w-8 h-8 hover:bg-[#A85751]/10 transition-colors"><Trash2 size={15} /></button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {viewer && (
        <div data-testid="doc-viewer" onClick={closeViewer} className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3">
          <div className="relative w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeViewer} data-testid="doc-viewer-close" aria-label={C.close} className="absolute top-2 right-2 z-10 rounded-full bg-white/90 p-1.5 text-[#075E62] shadow"><X size={18} /></button>
            <img src={viewer.url} alt="" onClick={() => setZoom((z) => !z)} className={`mx-auto rounded-lg ${zoom ? "max-w-none w-[200%] cursor-zoom-out" : "w-full cursor-zoom-in"}`} />
          </div>
        </div>
      )}

      {delDoc && (
        <div data-testid="doc-delete-modal" onClick={() => !deleting && setDelDoc(null)} className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-[#D5E7E5] bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <span className="shrink-0 w-10 h-10 rounded-full bg-[#A85751]/10 flex items-center justify-center"><Trash2 size={18} className="text-[#A85751]" /></span>
              <p className="text-sm font-semibold text-[#075E62] pt-1.5">{delDoc.tx_id ? C.delLinked : C.delQ}</p>
            </div>
            <div className="flex flex-col gap-2">
              {delDoc.tx_id ? (
                <>
                  <button type="button" disabled={deleting} onClick={() => removeDoc(false)} data-testid="doc-delete-doc-only" className={btn.accent + " w-full"}>{C.delDocOnly}</button>
                  <button type="button" disabled={deleting} onClick={() => removeDoc(true)} data-testid="doc-delete-doc-tx" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#A85751] text-white px-4 py-2 text-sm font-semibold hover:bg-[#8f453f] transition-colors disabled:opacity-60">{C.delDocTx}</button>
                </>
              ) : (
                <button type="button" disabled={deleting} onClick={() => removeDoc(false)} data-testid="doc-delete-confirm" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#A85751] text-white px-4 py-2 text-sm font-semibold hover:bg-[#8f453f] transition-colors disabled:opacity-60">{C.del}</button>
              )}
              <button type="button" disabled={deleting} onClick={() => setDelDoc(null)} data-testid="doc-delete-cancel" className={btn.outline + " w-full"}>{C.cancel}</button>
            </div>
          </div>
        </div>
      )}
      {renameDoc && (
        <div data-testid="doc-rename-modal" onClick={() => !renaming && setRenameDoc(null)} className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-[#D5E7E5] bg-white p-5 shadow-xl">
            <p className="text-sm font-semibold text-[#075E62] mb-3">{C.renameTitle}</p>
            <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && renameVal.trim()) doRename(); }} className={input} data-testid="doc-rename-input" placeholder={C.namePh} />
            <div className="flex flex-col gap-2 mt-4">
              <button type="button" disabled={renaming || !renameVal.trim()} onClick={doRename} data-testid="doc-rename-save" className={btn.accent + " w-full"}>{C.save}</button>
              <button type="button" disabled={renaming} onClick={() => setRenameDoc(null)} data-testid="doc-rename-cancel" className={btn.outline + " w-full"}>{C.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
